/**
 * The icon vocabulary: which glyph each concept uses, for every TurboTwin
 * client (webclient, viewer, portal, Unreal). This file is the single source
 * of truth. Every tab, chip, count, header, list, menu entry, sidebar item,
 * page header and dashboard widget that names a content kind shows
 * `content.<kind>`, every one that names a view (page) shows `view.<view>`,
 * every button that performs a common action shows `action.<action>`, and
 * every status badge shows `status.<status>`, so each app shows one icon per
 * concept everywhere.
 *
 * Entries are Material Icons names (the filled set, as the webclient uses
 * them); `paths.generated.ts` holds their 24x24 path data. A custom entry is
 * `{ path }`: a glyph Material does not have, drawn as filled path data in
 * the same 24px grid.
 *
 * Reserved meanings: the cube (ViewInAr) is the 3D viewer only, Hub is a twin,
 * DeviceHub is the graph view, Timeline (the line chart) is time scrubbing,
 * and AutoAwesome (`view.renders`) is AI Renders only. The globe (Public) is
 * public visibility only: the web client is `content.webClient` (Web).
 *
 * Visibility: `status.public` (the globe) and `status.private` (the padlock)
 * are a pair. They are the visibility of a project or an annotation.
 * `status.readOnly` (EditOff) marks something you can read but not change.
 * `action.show` / `action.hide` toggle whether something is drawn; they are
 * not that pair.
 *
 * Action rules: `delete` destroys data or revokes access (removing a project
 * member, a CORS origin, a GitHub repository, a layer from the project) and is
 * shown in the error colour; `remove` only drops a row or item from a form or
 * list (or cancels an invitation) and stays in the default colour; `unlink`
 * breaks a link and keeps both ends; `details` opens a details view; `purge`
 * is a permanent delete with no restore; `deleteMany` deletes in bulk.
 * `settings` opens settings, `tune` adjusts the current view or transform.
 * `resetView` restores a default camera or layout, `fitView` frames
 * everything, `focus` frames one thing.
 */
export const ICON_VOCABULARY = {
  /** One icon per kind of twin content. */
  content: {
    twin: 'Hub',
    model: 'AccountTree',
    property: 'DataObject',
    relationship: 'Link',
    component: 'Extension',
    telemetry: 'Sensors',
    visualAsset: 'Inventory2',
    location: 'Place',
    history: 'History',
    /** A person: a user, member or player. */
    user: 'Person',
    /** An AI agent. */
    agent: 'SmartToy',
    /** The Unreal client. */
    unrealClient: 'SportsEsports',
    /** The web client. Not the globe: that is public visibility. */
    webClient: 'Web',
  },
  /** One icon per view: a page, sidebar entry, dashboard widget or viewer rail. */
  view: {
    dashboard: 'Dashboard',
    graph: 'DeviceHub',
    map: 'Map',
    viewer3d: 'ViewInAr',
    import: 'UploadFile',
    history: 'History',
    simulator: 'Science',
    connector: 'Cable',
    restApi: 'Http',
    telemetry: 'Sensors',
    alerts: 'NotificationsActive',
    channels: 'AltRoute',
    reducers: 'Functions',
    assets: 'Inventory2',
    renders: 'AutoAwesome',
    projectConfig: 'Tune',
    projects: 'Folder',
    operations: 'MonitorHeart',
    services: 'Apps',
    modules: 'Widgets',
    activeDeployments: 'CloudDone',
    launcher: 'RocketLaunch',
    /** Who can open which project: the organization's Access page. */
    access: 'Key',
    /** The product docs (`/docs/`), opened in a new tab from the user menu. */
    docs: 'MenuBook',
    /** The camera button: opens the camera mode controls. */
    camera: 'Videocam',
    /** Orbit camera mode. */
    cameraOrbit: 'ThreeSixty',
    /** Walk camera mode. */
    cameraWalk: 'DirectionsWalk',
    /**
     * Free look (drone) camera mode. Material Icons has no drone: the
     * quadcopter formerly drawn inline in viewer-react's CameraControl (four rotor rings, arms, a square body), converted to filled path data
     * (four rotor rings, arms, body), converted from strokes to filled path data.
     */
    cameraFreeLook: {
      path:
        'M2 6a4 4 0 1 0 8 0a4 4 0 1 0-8 0ZM3.6 6a2.4 2.4 0 1 1 4.8 0a2.4 2.4 0 1 1-4.8 0Z' +
        'M14 6a4 4 0 1 0 8 0a4 4 0 1 0-8 0ZM15.6 6a2.4 2.4 0 1 1 4.8 0a2.4 2.4 0 1 1-4.8 0Z' +
        'M2 18a4 4 0 1 0 8 0a4 4 0 1 0-8 0ZM3.6 18a2.4 2.4 0 1 1 4.8 0a2.4 2.4 0 1 1-4.8 0Z' +
        'M14 18a4 4 0 1 0 8 0a4 4 0 1 0-8 0ZM15.6 18a2.4 2.4 0 1 1 4.8 0a2.4 2.4 0 1 1-4.8 0Z' +
        'M7.664 8.936L9.364 10.636L10.636 9.364L8.936 7.664ZM15.064 7.664L13.364 9.364L14.636 10.636L16.336 8.936Z' +
        'M8.936 16.336L10.636 14.636L9.364 13.364L7.664 15.064ZM16.336 15.064L14.636 13.364L13.364 14.636L15.064 16.336Z' +
        'M10.7 9.5a1.2 1.2 0 0 0-1.2 1.2v2.6a1.2 1.2 0 0 0 1.2 1.2h2.6a1.2 1.2 0 0 0 1.2-1.2v-2.6a1.2 1.2 0 0 0-1.2-1.2Z',
    },
  },
  /** One icon per common action button. See the action rules above. */
  action: {
    delete: 'DeleteOutline',
    remove: 'Close',
    purge: 'DeleteForever',
    deleteMany: 'DeleteSweep',
    upload: 'Upload',
    /** A picker or drop zone that clearly takes a file. */
    uploadFile: 'UploadFile',
    download: 'Download',
    fullscreen: 'OpenInFull',
    exitFullscreen: 'CloseFullscreen',
    settings: 'Settings',
    tune: 'Tune',
    edit: 'Edit',
    fitView: 'FitScreen',
    resetView: 'RestartAlt',
    focus: 'CenterFocusStrong',
    create: 'Add',
    deploy: 'CloudUpload',
    details: 'OpenInNew',
    unlink: 'LinkOff',
    /** Enter or leave time scrubbing (the reserved Timeline icon). */
    timeline: 'Timeline',
    /** Arm the viewer's measure tool (the ruler). */
    measure: 'Straighten',
    /** The measure tool's multi-segment (polyline) switch. */
    polyline: 'Polyline',
    /**
     * The measure tool's snap switch (the magnet). Material Icons has no
     * magnet: the horseshoe formerly drawn inline in viewer-react's contentIcons (outline with two pole bars), converted to filled path data
     * (a 1.8 stroke outline with two pole bars), converted to filled path data.
     */
    snap: {
      path:
        'M4.1 2.1V12a7.9 7.9 0 0 0 15.8 0V2.1h-6.3V12a1.6 1.6 0 0 1-3.2 0V2.1Z' +
        'M8.6 3.9V12a3.4 3.4 0 0 0 6.8 0V3.9h2.7V12a6.1 6.1 0 0 1-12.2 0V3.9Z' +
        'M9.5 6.6H5V8.4h4.5ZM19 6.6h-4.5v1.8H19Z',
    },
    /** Duplicate something, or copy it to the clipboard. */
    copy: 'ContentCopy',
    search: 'Search',
    /** The overflow menu: vertical dots everywhere, never horizontal. */
    more: 'MoreVert',
    play: 'PlayArrow',
    pause: 'Pause',
    stop: 'Stop',
    /** Run something again from its start: the Sightline play-again button, the telemetry Replay tab (resetView is the camera). */
    replay: 'Replay',
    /** A visibility toggle (draw it), not the public / private pair. */
    show: 'Visibility',
    /** A visibility toggle (hide it), not the public / private pair. */
    hide: 'VisibilityOff',
    /** The grip of a draggable row or panel. */
    dragHandle: 'DragIndicator',
    /** Report a bug or send feedback. */
    reportBug: 'PestControl',
    /** Drive a drawn line at eye height (the line of sight rows). */
    drive: 'DirectionsCar',
    /** Arm the Sightline tool: drive a line looking at a target (an eye in a frame; Visibility is show). */
    sightline: 'Preview',
    /** Run a path the other way (the Sightline reverse toggle). */
    reverse: 'SwapHoriz',
    /** Take the next click in the 3D scene as a pick (the eyedropper). */
    pickInScene: 'Colorize',
  },
  /**
   * One filled icon per status. "Not configured" is a warning, not an error.
   * `public` and `private` are a pair, the visibility of a project or an annotation.
   */
  status: {
    success: 'CheckCircle',
    error: 'Error',
    warning: 'Warning',
    info: 'Info',
    public: 'Public',
    private: 'Lock',
    /** Readable here but not editable (an asset shared from another project). Not the padlock: that is visibility. */
    readOnly: 'EditOff',
  },
} as const;
